import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import useApis from "../apis/useApis";
import appConstant from "./../json/appConstant.json";
import "./MapCanvas.css";
import { GoogleMap, LoadScript, Polygon, Marker } from "@react-google-maps/api";
import dayjs from "dayjs";

const GOOGLE_MAPS_API_KEY = "AIzaSyDISx453LwfqG2WZn-W-BvTvgzzliAUwlE"; // Replace with your API key

// Convert [lng, lat] => { lat, lng }
const convertCoordinates = (coords) =>
  coords.map((coord) => ({ lat: coord[1], lng: coord[0] }));

const WARD_COLORS = {
  "Sabzi Mandi": "#9C27B0",
  "Krishna Nagar": "#C5E1A5",
  "Pure Baba": "#7CB300",
  "Ghosiyana": "#12BDBD",
  "Pure Devi": "#02886D",
  "Chaman Ganj": "#F57C00",
  "Korihai": "#87CEAC",
  "Chikwahi Mandi": "#7CB342",
  "Sadar Bazar": "#0F9DBC",
  "Mahesh Khera": "#6758B7",
  "Pandareeba": "#FBC02D",
  "Soodan Khera": "#FFCC80",
  "Domahi": "#0157FF",
  "Ali Nagar": "#A1C2FA",
  "Nai Bazar": "#FFEA00",
};

const LALGANJ_CENTER_COORDS = { lat: 26.169, lng: 80.975 };
const INITIAL_MAP_ZOOM = 13;

const MapCanvas = () => {
  const mapRef = useRef(null);
  const [data, setData] = useState([]);
  const [selectedId, setSelectedId] = useState(""); // State for the selected ward ID
  const [mapInstance, setMapInstance] = useState(null); // State to hold the map instance
  const [wardGeoJson, setWardGeoJson] = useState(null); // State to hold GeoJSON data
  const { jsonApi } = useApis();

  const markers = useRef([]);
  const infoWindowRef = useRef(null);
  const imgUrl = process.env.REACT_APP_IMG_URL;
  const DEFAULT_IMAGE = "/assets/default.jpg";

  const clearAllMarkers = useCallback(() => {
    markers.current.forEach((marker) => marker.setMap(null));
    markers.current = [];
  }, []); // No dependencies as it only mutates a ref

  const renderMarkers = useCallback(
    (locations) => {
      if (!mapInstance || !window.google || !window.google.maps) {
        console.warn(
          "renderMarkers called before map instance or Google Maps API is ready."
        );
        return;
      }

      clearAllMarkers();
      const bounds = new window.google.maps.LatLngBounds();

      locations.forEach((location) => {
        const surveys = location.surveys || [];

        surveys.forEach((survey) => {
          const {
            latitude,
            longitude,
            WardName,
            address,
            phone_no,
            // Id, // Id is not used
            frontImageHouse,
            MohallaName,
            area_of_building_plot,
            name,
            created_at, // created_at is not used
            user,
            houseId,
            id
          } = survey;

          if (latitude && longitude) {
            const lat = parseFloat(latitude);
            const lng = parseFloat(longitude);

            const marker = new window.google.maps.Marker({
              position: { lat, lng },
              map: mapInstance,
            });

            markers.current.push(marker);
            bounds.extend({ lat, lng });

            const imageSrc =
              frontImageHouse && frontImageHouse.trim() !== ""
                ? `${imgUrl}/${frontImageHouse}`
                : DEFAULT_IMAGE;

            const contentString = `<div style="display: flex; gap: 10px; align-items: flex-start; position: relative;">
              <button id="closeInfoWindowBtn" style="position: absolute; top: 0; right: 0; background: transparent; border: none; font-size: 20px; cursor: pointer;">&times;</button>
              <div style="flex: 1;">
                <p><strong>House ID:</strong> ${houseId}</p>
                <p><strong>Name:</strong> ${name}</p>
                <p><strong>Phone Number:</strong> ${phone_no}</p>
                <p><strong>Area(in sq.m.):</strong> ${area_of_building_plot}</p>
                <p><strong>Address:</strong> ${address}</p>
                <p><strong>Ward:</strong> ${WardName || "N/A"}</p>
                <p><strong>Mohalla:</strong> ${MohallaName || "N/A"}</p>
                <p><strong>Surveyor:</strong> ${user}</p>
                <p><strong>Created At:</strong> ${created_at ? dayjs(created_at).format('DD-MM-YYYY HH:mm:ss') : ''}</p>
                <div style="display: flex; justify-content: center;">
                  <button 
                    style="margin-top: 10px;margin-left:200px; padding: 6px 12px; background-color: #007BFF; color: white; border: none; border-radius: 4px; cursor: pointer;" 
                    onclick="window.open('/survey/${id}', '_blank')">
                    View Survey
                  </button>
                </div>
              </div>
              <div style="flex-shrink: 0;">
                <img src="${imageSrc}" onerror="this.onerror=null; this.src='${DEFAULT_IMAGE}'" alt="House Front" style="max-width: 200px; height: auto; border-radius: 4px;">
              </div>
            </div>`;

            let isMouseOverMarker = false;
            let isMouseOverInfoWindow = false;
            
            if (infoWindowRef.current) {
              marker.addListener("mouseover", () => {
                isMouseOverMarker = true;
                infoWindowRef.current.setContent(contentString);
                infoWindowRef.current.open(mapInstance, marker);
            
                // Add event after DOM is rendered
                window.google.maps.event.addListenerOnce(infoWindowRef.current, 'domready', () => {
                  const infoWindowElement = document.querySelector('.gm-style-iw');
                  if (infoWindowElement) {
                    infoWindowElement.addEventListener('mouseenter', () => {
                      isMouseOverInfoWindow = true;
                    });
                    infoWindowElement.addEventListener('mouseleave', () => {
                      isMouseOverInfoWindow = false;
                      // Delay closing to allow smooth transition
                      setTimeout(() => {
                        if (!isMouseOverMarker && !isMouseOverInfoWindow) {
                          infoWindowRef.current.close();
                        }
                      }, 200);
                    });
                  }
            
                  const closeBtn = document.getElementById('closeInfoWindowBtn');
                  if (closeBtn) {
                    closeBtn.onclick = () => infoWindowRef.current.close();
                  }
                });
              });
            
              marker.addListener("mouseout", () => {
                isMouseOverMarker = false;
                // Delay to allow checking if mouse entered popup
                setTimeout(() => {
                  if (!isMouseOverMarker && !isMouseOverInfoWindow) {
                    infoWindowRef.current.close();
                  }
                }, 200);
              });
            }
          }
        });
      });

      if (locations.length > 0 && !bounds.isEmpty()) {
        mapInstance.fitBounds(bounds);
        window.google.maps.event.addListenerOnce(
          mapInstance,
          "bounds_changed",
          () => {
            if (mapInstance.getZoom() > 18) mapInstance.setZoom(18); // Adjusted max zoom
          }
        );
      } else if (locations.length === 0) {
        // Optionally reset map to default view if no locations
        mapInstance.setCenter(LALGANJ_CENTER_COORDS);
        mapInstance.setZoom(INITIAL_MAP_ZOOM);
      }
    },
    [mapInstance, clearAllMarkers, imgUrl, DEFAULT_IMAGE, infoWindowRef]
  );

  // Fetch ward GeoJSON data (replace with your actual API endpoint)
  useEffect(() => {
    // This is not an async fetch but direct assignment
    const hardcodedGeoJson = {
      type: "FeatureCollection",
      features: [
        { "type": "Feature", "properties": { "Name": "Sabzi Mandi" }, "geometry": { "type": "Polygon", "coordinates": [[[80.975486, 26.169865], [80.975097, 26.169431], [80.97467, 26.169002], [80.973811, 26.168161], [80.973704, 26.168051], [80.973584, 26.167941], [80.973362, 26.167737], [80.972949, 26.16779], [80.972697, 26.167833], [80.972504, 26.167641], [80.971797, 26.166856], [80.9716, 26.166638], [80.971406, 26.166429], [80.971275, 26.166261], [80.971964, 26.165899], [80.971894, 26.165628], [80.97206, 26.165418], [80.972143, 26.165306], [80.972186, 26.165222], [80.972303, 26.165023], [80.972358, 26.164955], [80.97251, 26.164828], [80.972451, 26.164717], [80.972728, 26.164412], [80.973051, 26.164705], [80.973354, 26.164985], [80.97361, 26.165199], [80.973784, 26.165315], [80.973885, 26.165407], [80.973882, 26.165438], [80.973548, 26.165677], [80.973407, 26.16576], [80.973187, 26.16588], [80.973437, 26.16618], [80.973584, 26.166351], [80.973629, 26.166407], [80.973737, 26.166515], [80.973941, 26.166704], [80.974185, 26.166862], [80.974585, 26.167056], [80.975124, 26.167209], [80.975725, 26.167411], [80.976183, 26.167566], [80.977377, 26.167503], [80.977546, 26.167499], [80.975486, 26.169865]]] } },
        { "type": "Feature", "properties": { "Name": "Krishna Nagar" }, "geometry": { "type": "Polygon", "coordinates": [[[80.967462, 26.161816], [80.966721, 26.161136], [80.966265, 26.160707], [80.966984, 26.160071], [80.967392, 26.159898], [80.969055, 26.160004], [80.969634, 26.159763], [80.970578, 26.159436], [80.972821, 26.158646], [80.974151, 26.157683], [80.975203, 26.157356], [80.976147, 26.157414], [80.976361, 26.158184], [80.976812, 26.158781], [80.978571, 26.160283], [80.979151, 26.160726], [80.978056, 26.161516], [80.977413, 26.160996], [80.976876, 26.161381], [80.97413, 26.164443], [80.974087, 26.16479], [80.97413, 26.165002], [80.973784, 26.165315], [80.97361, 26.165199], [80.973354, 26.164985], [80.972728, 26.164412], [80.973464, 26.163384], [80.973078, 26.163018], [80.972692, 26.163249], [80.972644, 26.163211], [80.972552, 26.163259], [80.97251, 26.163216], [80.972354, 26.163312], [80.972376, 26.163379], [80.972228, 26.163438], [80.972247, 26.163485], [80.972086, 26.163557], [80.971059, 26.160697], [80.971029, 26.160624], [80.970639, 26.160708], [80.970317, 26.160787], [80.970001, 26.160865], [80.969672, 26.160939], [80.969376, 26.161024], [80.968638, 26.161255], [80.9681, 26.16148], [80.967845, 26.161602], [80.967462, 26.161816]]] } },
        { "type": "Feature", "properties": { "Name": "Pure Baba" }, "geometry": { "type": "Polygon", "coordinates": [[[80.981537, 26.162703], [80.977546, 26.167499], [80.976183, 26.167566], [80.976022, 26.167528], [80.97554, 26.167345], [80.974585, 26.167056], [80.974091, 26.166825], [80.973619, 26.166411], [80.973187, 26.16588], [80.973548, 26.165677], [80.973882, 26.165438], [80.973885, 26.165407], [80.973784, 26.165315], [80.97413, 26.165002], [80.974087, 26.16479], [80.97413, 26.164443], [80.976876, 26.161381], [80.977413, 26.160996], [80.978056, 26.161516], [80.979151, 26.160726], [80.981537, 26.162703]]] } },
        { "type": "Feature", "properties": { "Name": "Ghosiyana" }, "geometry": { "type": "Polygon", "coordinates": [[[80.975486, 26.169865], [80.973072, 26.172684], [80.972471, 26.172221], [80.97275, 26.171952], [80.972439, 26.171769], [80.972932, 26.171297], [80.972331, 26.17019], [80.972482, 26.170122], [80.972128, 26.169535], [80.97216, 26.169419], [80.972589, 26.16927], [80.972739, 26.169198], [80.972857, 26.169106], [80.97297, 26.168938], [80.973125, 26.168736], [80.97319, 26.168721], [80.97334, 26.168842], [80.973447, 26.16876], [80.973512, 26.168692], [80.972697, 26.167833], [80.973362, 26.167737], [80.973699, 26.168047], [80.973864, 26.168219], [80.974654, 26.169042], [80.975486, 26.169865]]] } },
        { "type": "Feature", "properties": { "Name": "Pure Devi" }, "geometry": { "type": "Polygon", "coordinates": [[[80.970106, 26.170906], [80.970534, 26.170512], [80.971147, 26.170944], [80.971788, 26.171371], [80.971922, 26.171184], [80.972029, 26.171054], [80.972169, 26.170943], [80.972308, 26.170933], [80.972383, 26.170991], [80.972651, 26.170789], [80.972927, 26.171285], [80.972735, 26.171449], [80.972541, 26.171635], [80.972439, 26.171769], [80.97275, 26.171952], [80.972471, 26.172221], [80.973072, 26.172684], [80.971044, 26.175048], [80.970465, 26.174894], [80.970079, 26.174836], [80.9698, 26.174547], [80.969736, 26.17422], [80.969392, 26.173931], [80.969264, 26.173199], [80.969349, 26.172641], [80.969306, 26.172256], [80.969585, 26.17214], [80.969778, 26.17266], [80.9701, 26.17241], [80.97025, 26.17216], [80.970372, 26.17196], [80.970477, 26.171842], [80.970601, 26.171737], [80.970703, 26.171654], [80.970747, 26.171611], [80.970858, 26.171495], [80.970475, 26.171215], [80.970106, 26.170906]]] } },
        { "type": "Feature", "properties": { "Name": "Chaman Ganj" }, "geometry": { "type": "Polygon", "coordinates": [[[80.970079, 26.174836], [80.970042, 26.175163], [80.969876, 26.17577], [80.969764, 26.17603], [80.969495, 26.176429], [80.968788, 26.177166], [80.968487, 26.177243], [80.968058, 26.177724], [80.96794, 26.177772], [80.967747, 26.177532], [80.967286, 26.176867], [80.967414, 26.176155], [80.967178, 26.174336], [80.966913, 26.173092], [80.968158, 26.172256], [80.969365, 26.17144], [80.970106, 26.170906], [80.970475, 26.171215], [80.970858, 26.171495], [80.970713, 26.171644], [80.970477, 26.171842], [80.970372, 26.17196], [80.970269, 26.172121], [80.9701, 26.17241], [80.969778, 26.17266], [80.969585, 26.17214], [80.969306, 26.172256], [80.969349, 26.172641], [80.969264, 26.173199], [80.969392, 26.173931], [80.969736, 26.17422], [80.9698, 26.174547], [80.970079, 26.174836]]] } },
        { "type": "Feature", "properties": { "Name": "Korihai" }, "geometry": { "type": "Polygon", "coordinates": [[[80.971586, 26.169027], [80.971377, 26.168839], [80.971329, 26.168801], [80.970342, 26.169557], [80.970261, 26.16959], [80.970138, 26.16959], [80.96981, 26.169797], [80.969735, 26.169687], [80.969623, 26.169552], [80.969505, 26.169422], [80.969349, 26.169268], [80.969285, 26.169162], [80.969279, 26.169075], [80.969306, 26.168777], [80.969258, 26.16868], [80.969038, 26.168358], [80.968977, 26.168256], [80.968848, 26.168121], [80.968925, 26.167973], [80.969124, 26.167814], [80.969338, 26.167668], [80.969548, 26.167491], [80.969703, 26.167347], [80.969769, 26.167266], [80.969546, 26.167068], [80.96933, 26.166866], [80.969525, 26.166721], [80.969658, 26.166623], [80.969695, 26.166595], [80.969707, 26.166577], [80.969704, 26.166553], [80.969923, 26.166422], [80.97004, 26.166394], [80.970191, 26.166591], [80.970492, 26.166938], [80.970626, 26.167123], [80.970846, 26.167289], [80.97099, 26.167383], [80.971267, 26.167754], [80.971374, 26.167866], [80.971495, 26.167977], [80.971816, 26.168221], [80.972014, 26.168376], [80.971838, 26.168637], [80.971586, 26.169061], [80.971586, 26.169027]]] } },
        { "type": "Feature", "properties": { "Name": "Chikwahi Mandi" }, "geometry": { "type": "Polygon", "coordinates": [[[80.971769, 26.165329], [80.971658, 26.165398], [80.971606, 26.16543], [80.971555, 26.165466], [80.971416, 26.165565], [80.971056, 26.165751], [80.970806, 26.165872], [80.970565, 26.166026], [80.970323, 26.166166], [80.970178, 26.166272], [80.97011, 26.166332], [80.97004, 26.166394], [80.969957, 26.16631], [80.969875, 26.166226], [80.969617, 26.165948], [80.969454, 26.165764], [80.969345, 26.165648], [80.969246, 26.165526], [80.969228, 26.165503], [80.969212, 26.165478], [80.96919, 26.165452], [80.969176, 26.165434], [80.969152, 26.165404], [80.969114, 26.165358], [80.969093, 26.165333], [80.96907, 26.165306], [80.969049, 26.165281], [80.969035, 26.165262], [80.969077, 26.165242], [80.969231, 26.165167], [80.969307, 26.165129], [80.96947, 26.165092], [80.969665, 26.165049], [80.969852, 26.165016], [80.970048, 26.164984], [80.970245, 26.164948], [80.970551, 26.164893], [80.970868, 26.164827], [80.971015, 26.164789], [80.971081, 26.164772], [80.971317, 26.164865], [80.971369, 26.164887], [80.971461, 26.164965], [80.971581, 26.165069], [80.971624, 26.165116], [80.971722, 26.165239], [80.971769, 26.165329]]] } },
        { "type": "Feature", "properties": { "Name": "Sadar Bazar" }, "geometry": { "type": "Polygon", "coordinates": [[[80.972014, 26.168376], [80.971816, 26.168238], [80.971703, 26.168141], [80.971462, 26.167954], [80.971188, 26.167674], [80.97099, 26.1674], [80.970818, 26.167251], [80.970625, 26.16714], [80.970362, 26.166817], [80.970041, 26.166395], [80.970178, 26.166272], [80.970255, 26.16622], [80.970323, 26.166166], [80.970441, 26.166097], [80.970636, 26.165989], [80.970806, 26.165872], [80.970912, 26.165823], [80.97118, 26.16569], [80.971416, 26.165565], [80.971606, 26.16543], [80.971769, 26.165329], [80.971785, 26.165394], [80.97182, 26.165447], [80.971867, 26.165554], [80.971894, 26.165628], [80.971901, 26.165658], [80.97192, 26.165728], [80.971954, 26.16586], [80.971964, 26.165899], [80.971505, 26.166153], [80.97129, 26.166278], [80.971381, 26.166401], [80.971455, 26.166478], [80.971528, 26.16656], [80.97167, 26.166714], [80.971937, 26.16701], [80.972205, 26.167313], [80.972559, 26.167703], [80.972697, 26.167833], [80.972549, 26.167888], [80.972389, 26.167948], [80.972225, 26.168105], [80.972115, 26.168244], [80.972014, 26.168376]]] } },
        { "type": "Feature", "properties": { "Name": "Mahesh Khera" }, "geometry": { "type": "Polygon", "coordinates": [[[80.964511, 26.166077], [80.964334, 26.165947], [80.964152, 26.165846], [80.963926, 26.165716], [80.9628, 26.164876], [80.963218, 26.16453], [80.962735, 26.164106], [80.962585, 26.164058], [80.962027, 26.163596], [80.961652, 26.163249], [80.966265, 26.160707], [80.967494, 26.161848], [80.968435, 26.162739], [80.9694, 26.163619], [80.969879, 26.164049], [80.970364, 26.164458], [80.970795, 26.164648], [80.971077, 26.164772], [80.970524, 26.164913], [80.970036, 26.164985], [80.969296, 26.165134], [80.969048, 26.165281], [80.968969, 26.165139], [80.968979, 26.164937], [80.968834, 26.164998], [80.968719, 26.165138], [80.96881, 26.165189], [80.968862, 26.165231], [80.968913, 26.165292], [80.968804, 26.165355], [80.968592, 26.165456], [80.968487, 26.165478], [80.968431, 26.165367], [80.968267, 26.165184], [80.968224, 26.165145], [80.968353, 26.165008], [80.968184, 26.164911], [80.968012, 26.164808], [80.967776, 26.164616], [80.96733, 26.164303], [80.967217, 26.164067], [80.967152, 26.16386], [80.967174, 26.163658], [80.966858, 26.163176], [80.96638, 26.163687], [80.966584, 26.164058], [80.965779, 26.164679], [80.965618, 26.165459], [80.965414, 26.165449], [80.964765, 26.165757], [80.964776, 26.165815], [80.96468, 26.165873], [80.964771, 26.166046], [80.96469, 26.166089], [80.964583, 26.166008], [80.964511, 26.166077]]] } },
        { "type": "Feature", "properties": { "Name": "Pandareeba" }, "geometry": { "type": "Polygon", "coordinates": [[[80.963197, 26.166686], [80.963554, 26.166186], [80.963926, 26.165716], [80.964152, 26.165846], [80.96435, 26.165956], [80.964511, 26.166077], [80.964583, 26.166008], [80.96469, 26.166089], [80.964771, 26.166046], [80.96468, 26.165873], [80.964776, 26.165815], [80.964765, 26.165757], [80.965414, 26.165449], [80.965618, 26.165459], [80.965779, 26.164679], [80.966584, 26.164058], [80.96638, 26.163687], [80.966858, 26.163176], [80.967174, 26.163658], [80.967152, 26.16386], [80.96733, 26.164303], [80.968353, 26.165008], [80.968224, 26.165145], [80.968329, 26.165255], [80.968431, 26.165367], [80.968487, 26.165478], [80.968592, 26.165456], [80.968804, 26.165355], [80.968892, 26.165306], [80.968913, 26.165292], [80.968862, 26.165231], [80.96881, 26.165189], [80.968743, 26.16515], [80.968719, 26.165138], [80.968834, 26.164998], [80.968979, 26.164937], [80.968969, 26.165139], [80.969035, 26.165262], [80.969228, 26.165503], [80.969345, 26.165648], [80.96938, 26.165686], [80.969718, 26.166062], [80.969875, 26.166226], [80.969957, 26.16631], [80.97004, 26.166394], [80.969923, 26.166422], [80.96972, 26.16654], [80.969704, 26.166553], [80.969707, 26.166577], [80.969695, 26.166595], [80.96933, 26.166866], [80.969244, 26.166777], [80.968965, 26.166512], [80.96877, 26.166378], [80.96862, 26.166252], [80.968501, 26.166179], [80.968396, 26.16609], [80.968287, 26.165925], [80.968179, 26.165651], [80.968137, 26.16553], [80.967303, 26.165698], [80.96642, 26.165877], [80.965658, 26.165973], [80.964994, 26.166166], [80.964278, 26.166403], [80.963986, 26.166505], [80.963492, 26.166628], [80.963197, 26.166686]]] } },
        { "type": "Feature", "properties": { "Name": "Soodan Khera" }, "geometry": { "type": "Polygon", "coordinates": [[[80.966466, 26.169045], [80.966742, 26.169337], [80.967108, 26.169668], [80.967202, 26.169602], [80.967449, 26.169366], [80.967572, 26.169205], [80.96762, 26.169081], [80.967688, 26.169026], [80.967907, 26.168906], [80.968464, 26.168562], [80.968977, 26.168256], [80.969003, 26.168294], [80.969306, 26.168777], [80.969282, 26.169142], [80.969349, 26.169268], [80.969505, 26.169422], [80.969692, 26.169628], [80.96981, 26.169797], [80.970138, 26.16959], [80.970261, 26.16959], [80.970342, 26.169557], [80.970754, 26.169252], [80.971329, 26.168801], [80.971586, 26.169027], [80.971586, 26.169061], [80.97132, 26.169516], [80.970934, 26.170114], [80.970106, 26.170906], [80.969365, 26.17144], [80.966913, 26.173092], [80.966873, 26.172953], [80.966824, 26.172672], [80.966764, 26.172408], [80.966656, 26.171508], [80.966544, 26.171007], [80.966404, 26.170502], [80.965943, 26.169731], [80.966466, 26.169045]]] } },
        { "type": "Feature", "properties": { "Name": "Domahi" }, "geometry": { "type": "Polygon", "coordinates": [[[80.971859, 26.165537], [80.97182, 26.165447], [80.97178, 26.165383], [80.971769, 26.165329], [80.971592, 26.165075], [80.971372, 26.164887], [80.971045, 26.164757], [80.970669, 26.164593], [80.970364, 26.164458], [80.970004, 26.16416], [80.968379, 26.162687], [80.967606, 26.161955], [80.967462, 26.161816], [80.967845, 26.161602], [80.968638, 26.161255], [80.969546, 26.160975], [80.970734, 26.160687], [80.970975, 26.160634], [80.971029, 26.160624], [80.972086, 26.163557], [80.972247, 26.163485], [80.972228, 26.163438], [80.972376, 26.163379], [80.972354, 26.163312], [80.97251, 26.163216], [80.972552, 26.163259], [80.972644, 26.163211], [80.972692, 26.163249], [80.973078, 26.163018], [80.973464, 26.163384], [80.972728, 26.164412], [80.972486, 26.16468], [80.972451, 26.164717], [80.97249, 26.164801], [80.97251, 26.164828], [80.972358, 26.164955], [80.972299, 26.165028], [80.972186, 26.165222], [80.972143, 26.165306], [80.972082, 26.165391], [80.971934, 26.165572], [80.971894, 26.165628], [80.971859, 26.165537]]] } }
      ],
    };
    setWardGeoJson(hardcodedGeoJson);
  }, []);

  // Fetch survey data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await jsonApi(appConstant.WardMap, {}, null, null);
        if (!res.error) {
          setData(res.data?.data?.wards || []); // Ensure data.data exists before accessing wards
        } else {
          console.error("Error fetching survey data:", res.message);
          setData([]); // Set to empty array on error
        }
      } catch (error) {
        console.error("Exception fetching survey data:", error);
        setData([]); // Set to empty array on exception
      }
    };
    fetchData();
  }, []); // Added jsonApi as a dependency

  const totalSurveyCount = useMemo(() => {
    // Ensure data is an array before attempting to reduce it
    if (!Array.isArray(data) || data.length === 0) {
      return 0;
    }
    return data.reduce((acc, location) => acc + (location?.surveys?.length || 0), 0);
  }, [data]);
  // Effect to render markers when map is loaded and data is available
  useEffect(() => {
    if (mapInstance) {
      // Call renderMarkers with the current data.
      // renderMarkers function has internal logic to handle empty data
      // (e.g., by setting a default zoom), which will make polygons visible.
      // If data is present, it will fit bounds to the markers.
      renderMarkers(data);
    }
  }, [mapInstance, data, renderMarkers]); // Added data to dependency array

  const handleViewMapClick = () => {
    if (!mapInstance) return;

    if (selectedId) {
      const selectedWard = data.find((d) => String(d.WardId) === selectedId);

      if (selectedWard) {
        renderMarkers([selectedWard]);
        // Optionally, you could also filter and display the corresponding polygon here
      } else {
        clearAllMarkers();
        // Optionally, clear any displayed polygons
      }
    } else {
      renderMarkers(data); // Show all markers
      // Optionally, display all polygons
    }
  };

  const handleDropdownChange = (e) => {
    setSelectedId(e.target.value);
  };

  return (
    <div className="map-canvas">
      <h2>Select a Ward</h2>
      {data.length > 0 ? (
        <div className="controls">
          <select
            onChange={handleDropdownChange}
            value={selectedId}
            className="select-dropdown"
          >
            <option value="">
              All Wards
            </option>
            {/* Corrected: Iterate over `data` directly, as it holds the array of wards */}
            {Array.isArray(data) && data.map((location) => (
              <option key={location.WardId} value={location.WardId}>
                {location.WardName} ({location.surveyCount})
              </option>
            ))}
          </select>
          <button className="view-map-button" onClick={handleViewMapClick}>
            View Map
          </button>
        </div>
      ) : (
        <p>Loading house data...</p>
      )}
      <h2>Google Map with Survey Houses ({totalSurveyCount})</h2>
      <div className="map-wrapper">
        {Object.keys(WARD_COLORS).length > 0 && (
          <div className="map-legend">
            <h4>Ward Legend</h4>
            <ul>
              {Object.entries(WARD_COLORS).map(([name, color]) => (
                <li key={name}>
                  <span
                    className="legend-color-box"
                    style={{ backgroundColor: color }}
                  ></span>
                  {name}
                </li>
              ))}
            </ul>
          </div>
        )}
        <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "600px" }} // Adjust height as needed
            zoom={INITIAL_MAP_ZOOM}
            center={LALGANJ_CENTER_COORDS} // Initial center
            onLoad={(loadedMap) => {
              setMapInstance(loadedMap);
              // Initialize InfoWindow only once and if google.maps is available
              if (window.google && window.google.maps && !infoWindowRef.current) {
                infoWindowRef.current = new window.google.maps.InfoWindow();
              } else if (!window.google || !window.google.maps) {
                console.error("Google Maps API not available for InfoWindow creation.");
              }
            }}
          >
            {wardGeoJson?.features?.map((feature, index) => {
              const path = convertCoordinates(feature.geometry.coordinates[0]);

              return (
                <Polygon
                  key={index}
                  path={path}
                  options={{
                    fillColor: WARD_COLORS[feature.properties.Name] || "lightblue", // Use mapped color or default
                    fillOpacity: 0.4,
                    strokeWeight: 0, // This will remove the outline
                    clickable: true,
                    editable: false,
                    draggable: false,
                    zIndex: 1,
                  }}
                />
              );
            })}
            {/* Markers will be rendered within the GoogleMap component now */}
          </GoogleMap>
        </LoadScript>
      </div>
    </div>
  );
};


export default MapCanvas;